import React from 'react';
import Icon from '../../components/AppIcon';
import { Link } from 'react-router-dom';
import TopNavigation from '../../components/TopNavigation';
import Footer from '../../components/Footer';
import DocsHeroSection from './components/DocsHeroSection';

const Documentation = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNavigation />
      
      {/* Hero Section */}
      <DocsHeroSection />

      {/* Quick Start Section */}
      <section className="py-24 md:py-32 px-4 md:px-6 lg:px-8 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
            Quick Start
          </h2>
          
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-12 max-w-3xl mx-auto text-center">
            Set up your first Sleeve Dock and connect to the Hub in just minutes. Our step-by-step guide walks you through hardware installation, network configuration, and your first remote connection.
          </p>
          
          {/* Quick Start Flow */}
          <div className="mb-12">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8 mb-8">
              {/* DOCK */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Icon name="Box" size={32} color="var(--color-primary)" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">DOCK</h3>
              </div>
              
              {/* Arrow */}
              <Icon name="ArrowRight" size={24} color="var(--color-primary)" className="hidden md:block" />
              <Icon name="ArrowDown" size={24} color="var(--color-primary)" className="md:hidden" />
              
              {/* Connect Dock */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                  <Icon name="Plug" size={32} color="var(--color-secondary)" />
                </div>
                <h3 className="text-base font-medium text-foreground">Connect Dock</h3>
              </div>
              
              {/* Arrow */}
              <Icon name="ArrowRight" size={24} color="var(--color-primary)" className="hidden md:block" />
              <Icon name="ArrowDown" size={24} color="var(--color-primary)" className="md:hidden" />
              
              {/* Network Setup */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-lg bg-secondary/10 flex items-center justify-center mb-4">
                  <Icon name="Wifi" size={32} color="var(--color-secondary)" />
                </div>
                <h3 className="text-base font-medium text-foreground">Network Setup</h3>
              </div>
              
              {/* Arrow */}
              <Icon name="ArrowRight" size={24} color="var(--color-primary)" className="hidden md:block" />
              <Icon name="ArrowDown" size={24} color="var(--color-primary)" className="md:hidden" />
              
              {/* HUB */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-lg bg-warning/10 flex items-center justify-center mb-4">
                  <Icon name="Server" size={32} color="var(--color-secondary)" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">HUB</h3>
              </div>
              
              {/* Arrow */}
              <Icon name="ArrowRight" size={24} color="var(--color-primary)" className="hidden md:block" />
              <Icon name="ArrowDown" size={24} color="var(--color-primary)" className="md:hidden" />
              
              {/* Hub Discovery */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Icon name="Search" size={32} color="var(--color-primary)" />
                </div>
                <h3 className="text-base font-medium text-foreground">Hub Discovery</h3>
              </div>
            </div>
          </div>
          
          {/* Terminal Snapshot */}
          <div className="max-w-3xl mx-auto">
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="bg-muted/30 px-4 py-2 border-b border-border flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="ml-2 text-xs text-muted-foreground font-mono">terminal</span>
              </div>
              <div className="p-6 font-mono text-sm">
                <div className="text-muted-foreground mb-2">$ sleeve connect --dock DOCK_001</div>
                <div className="text-green-500 mb-1">✓ Connected to Sleeve Hub</div>
                <div className="text-green-500 mb-1">✓ Workspace ready</div>
                <div className="text-foreground mb-2">Ready for remote access</div>
                <div className="text-muted-foreground inline-block animate-pulse">█</div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Successful Dock-to-Hub connection and workspace initialization.
            </p>
          </div>
          
          {/* Primary Link CTA */}
          <div className="mt-12 text-center">
            <a 
              href="https://docs.sleeve.dev/How-to-Guides/#troubleshooting-guides" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
            >
              View Quick Start Guide
              <Icon name="ArrowRight" size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Documentation Categories */}
      <section className="py-24 md:py-32 px-4 md:px-6 lg:px-8 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-6 text-center">
            Documentation Categories
          </h2>
          
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-12 max-w-3xl mx-auto text-center">
            Comprehensive guides for every aspect of Sleeve, from basic setup to enterprise deployment and advanced integrations.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Setup & Installation */}
            <Link 
              to="#setup-installation" 
              className="group bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all duration-250"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Package" size={24} color="var(--color-primary)" />
                </div>
                <Icon name="ArrowRight" size={20} color="var(--color-primary)" className="group-hover:text-primary transition-colors" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-3">
                Setup & Installation
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Get started with Sleeve Dock hardware setup, network configuration, and initial Hub connection in under 10 minutes.
              </p>
            </Link>
            
            {/* Using Sleeve Hub */}
            <Link 
              to="#using-hub" 
              className="group bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all duration-250"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Icon name="Server" size={24} color="var(--color-secondary)" />
                </div>
                <Icon name="ArrowRight" size={20} color="var(--color-secondary)" className="group-hover:text-primary transition-colors" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-3">
                Using Sleeve Hub
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Master Hub management, device discovery, workspace creation, and remote access controls for optimal team collaboration.
              </p>
            </Link>
            
            {/* Managing Docks */}
            <Link 
              to="#managing-docks" 
              className="group bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all duration-250"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center">
                  <Icon name="Box" size={24} color="var(--color-secondary)" />
                </div>
                <Icon name="ArrowRight" size={20} color="var(--color-secondary)" className="group-hover:text-primary transition-colors" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-3">
                Managing Docks
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Learn Dock deployment strategies, firmware updates, health monitoring, and troubleshooting for enterprise environments.
              </p>
            </Link>
            
            {/* Creating Workspaces */}
            <Link 
              to="#creating-workspaces" 
              className="group bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all duration-250"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Icon name="FolderOpen" size={24} color="var(--color-secondary)" />
                </div>
                <Icon name="ArrowRight" size={20} color="var(--color-secondary)" className="group-hover:text-primary transition-colors" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-3">
                Creating Workspaces
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Organize projects with workspace templates, role-based permissions, and collaborative development workflows.
              </p>
            </Link>
            
            {/* CI/CD Integration */}
            <Link 
              to="#cicd-integration" 
              className="group bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all duration-250"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="GitMerge" size={24} color="var(--color-primary)" />
                </div>
                <Icon name="ArrowRight" size={20} color="var(--color-secondary)" className="group-hover:text-primary transition-colors" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-3">
                CI/CD Integration
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Integrate Sleeve with Jenkins, GitHub Actions, GitLab CI, and custom automation pipelines for continuous hardware testing.
              </p>
            </Link>
            
            {/* Security & Compliance */}
            <Link 
              to="#security-compliance" 
              className="group bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all duration-250"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center">
                  <Icon name="Shield" size={24} color="var(--color-primary)" />
                </div>
                <Icon name="ArrowRight" size={20} color="var(--color-secondary)" className="group-hover:text-primary transition-colors" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-3">
                Security & Compliance
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Implement enterprise security policies, audit logging, data encryption, and compliance frameworks (SOC2, ISO 27001).
              </p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Documentation;